import { useCallback, useEffect, useRef, useState } from "react";
import { algoliasearch } from "algoliasearch";
import type { StoreSearchKeyResponse } from "../api/storesApi";
import type { StoreId, ChainId, ResellerId } from "@KassaroAB/shared/types/ids";

export type StoreSearchHit = {
  id: StoreId;
  name: string;
  displayName: string | undefined;
  orgNumber: string;
  active: boolean;
};

export const storeHitLabel = (store: StoreSearchHit) =>
  store.displayName || store.name;

const SEARCH_DEBOUNCE_MS = 300;
const HITS_PER_PAGE = 1000;

type AlgoliaClient = ReturnType<typeof algoliasearch>;

const algoliaClientByKey = new Map<string, AlgoliaClient>();

const getAlgoliaClient = (config: StoreSearchKeyResponse): AlgoliaClient => {
  const key = `${config.algoliaAppId}\0${config.searchKey}`;
  const existing = algoliaClientByKey.get(key);
  if (existing) {
    return existing;
  }
  const client = algoliasearch(config.algoliaAppId, config.searchKey);
  algoliaClientByKey.set(key, client);
  return client;
};

const searchStores = async (
  config: StoreSearchKeyResponse,
  query: string,
  activeOnly: boolean,
  chainId: ChainId | undefined,
  resellerId: ResellerId | undefined
): Promise<StoreSearchHit[]> => {
  const client = getAlgoliaClient(config);
  const facetFilters: string[][] = [];
  if (activeOnly) {
    facetFilters.push(["active:true"]);
  }
  if (chainId) {
    facetFilters.push([`chainId:${chainId}`]);
  }
  if (resellerId) {
    facetFilters.push([`resellerId:${resellerId}`]);
  }
  const response = await client.searchSingleIndex({
    indexName: config.indexName,
    searchParams: {
      query: query,
      facetFilters: facetFilters.length > 0 ? facetFilters : undefined,
      hitsPerPage: HITS_PER_PAGE,
    },
  });

  return (response.hits ?? []).map((hit: Record<string, unknown>) => ({
    id: hit.objectID as StoreId,
    name: (hit.name as string) ?? "",
    displayName: (hit.displayName as string | undefined) ?? undefined,
    orgNumber: (hit.orgNumber as string) ?? "",
    active: typeof hit.active === "boolean" ? hit.active : true,
  }));
};

export const useStoreSearch = (
  config: StoreSearchKeyResponse | null,
  options?: {
    chainId?: ChainId;
    resellerId?: ResellerId;
    defaultActiveOnly?: boolean;
    syncSearchUrl?: boolean;
    urlQuery?: string;
    urlActiveOnly?: boolean;
  }
) => {
  const chainId = options?.chainId;
  const resellerId = options?.resellerId;
  const defaultActiveOnly = options?.defaultActiveOnly ?? false;
  const syncSearchUrl = options?.syncSearchUrl ?? false;
  const urlQuery = options?.urlQuery;
  const urlActiveOnly = options?.urlActiveOnly;
  const [query, setQuery] = useState(() =>
    syncSearchUrl ? (urlQuery ?? "") : ""
  );
  const [activeOnly, setActiveOnly] = useState(() => {
    if (syncSearchUrl && urlActiveOnly !== undefined) {
      return urlActiveOnly;
    }
    return defaultActiveOnly;
  });
  const [hits, setHits] = useState<StoreSearchHit[]>([]);
  const [searchedQuery, setSearchedQuery] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const prevChainIdRef = useRef(chainId);
  const prevResellerIdRef = useRef(resellerId);
  const prevConfigRef = useRef<StoreSearchKeyResponse | null>(null);

  const clearDebounce = useCallback(() => {
    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  useEffect(() => () => clearDebounce(), [clearDebounce]);

  const search = useCallback(
    (searchQuery: string, activeOnlyFilter: boolean) => {
      if (!config) {
        return;
      }
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      setIsSearching(true);
      searchStores(config, searchQuery, activeOnlyFilter, chainId, resellerId)
        .catch((): StoreSearchHit[] => [])
        .then((result) => {
          if (requestId !== requestIdRef.current) {
            return;
          }
          setHits(result);
          setSearchedQuery(searchQuery);
          setIsSearching(false);
        });
    },
    [config, chainId, resellerId]
  );

  const searchRef = useRef(search);

  useEffect(() => {
    searchRef.current = search;
  }, [search]);

  useEffect(() => {
    if (!syncSearchUrl || urlQuery === undefined || urlActiveOnly === undefined) {
      return;
    }
    if (query === urlQuery && activeOnly === urlActiveOnly) {
      return;
    }
    const handle = window.setTimeout(() => {
      setQuery(urlQuery);
      setActiveOnly(urlActiveOnly);
      clearDebounce();
    }, 0);
    return () => window.clearTimeout(handle);
  }, [
    syncSearchUrl,
    urlQuery,
    urlActiveOnly,
    query,
    activeOnly,
    clearDebounce,
  ]);

  useEffect(() => {
    if (!syncSearchUrl || !config || urlQuery === undefined || urlActiveOnly === undefined) {
      return;
    }
    if (urlQuery.length === 0) {
      const handle = window.setTimeout(() => {
        setHits([]);
      }, 0);
      return () => window.clearTimeout(handle);
    }
    const handle = window.setTimeout(() => {
      searchRef.current(urlQuery, urlActiveOnly);
    }, 0);
    return () => window.clearTimeout(handle);
  }, [syncSearchUrl, config, urlQuery, urlActiveOnly, chainId, resellerId]);

  useEffect(() => {
    if (syncSearchUrl) {
      return;
    }
    const chainChanged = prevChainIdRef.current !== chainId;
    const resellerChanged = prevResellerIdRef.current !== resellerId;
    const configBecameAvailable = !prevConfigRef.current && !!config;
    prevChainIdRef.current = chainId;
    prevResellerIdRef.current = resellerId;
    prevConfigRef.current = config;
    if (!chainChanged && !resellerChanged && !configBecameAvailable) {
      return;
    }
    if (!config || query.length === 0) {
      return;
    }
    const handle = window.setTimeout(() => {
      search(query, activeOnly);
    }, 0);
    return () => window.clearTimeout(handle);
  }, [syncSearchUrl, chainId, resellerId, config, query, activeOnly, search]);

  const updateQuery = useCallback(
    (newQuery: string) => {
      setQuery(newQuery);
      clearDebounce();
      if (!config) {
        return;
      }
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        search(newQuery, activeOnly);
      }, SEARCH_DEBOUNCE_MS);
    },
    [activeOnly, search, config, clearDebounce]
  );

  const toggleActiveOnly = useCallback(() => {
    clearDebounce();
    const newActiveOnly = !activeOnly;
    setActiveOnly(newActiveOnly);
    search(query, newActiveOnly);
  }, [activeOnly, query, search, clearDebounce]);

  return {
    query,
    updateQuery,
    activeOnly,
    toggleActiveOnly,
    hits,
    isSearching,
    searchedQuery,
    search: (q?: string) => search(q ?? query, activeOnly),
  };
};
